'use client';
import { currentAccountAtom, currentUserAtom, freshdeskContextAtom } from '@/app/states/atoms';
import { AdminPages } from '@/features/Admin/AdminNav';
import { HeaderSearch } from '@/features/HeaderSearch';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bars3Icon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { useLogout } from 'auth';
import HeaderLogo from 'fiona-assets/images/Fiona _small_logo.svg';
import { useAtom, useAtomValue } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { openSupportForm } from 'utils/Freshdesk';
import { FRESHDESK_WIDGETS_ID } from 'utils/Freshdesk/constants';
import { useFreshdeskWidget } from 'utils/Freshdesk/useFreshdeskWidget';
import { AccountSwitcher } from '../AccountSwitcher';
import { MainNavigation } from '../MainNavigation';
import { Protect } from '../Protect';

export default function Header(props: { title: string; hideSearch?: boolean }) {
  const { title, hideSearch = false } = props;
  const pathName = usePathname();
  const logout = useLogout();
  const [currentAccount] = useAtom(currentAccountAtom);
  const [freshdeskContext] = useAtom(freshdeskContextAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);

  const activeWidget =
    currentAccount?.name === 'DIGIBLE' && pathName.includes('onboarding') ? 'onboarding' : 'posts';

  const roles = currentAccount?.roles ?? [];
  const isSystemAdmin = roles.includes('SYSTEM:ADMIN');
  const isAgencyUser = roles.includes('AGENCY:USER');
  const adminHref = isSystemAdmin
    ? AdminPages.USERS.href
    : isAgencyUser
      ? AdminPages.GBP_ADMIN.href
      : '/';

  useFreshdeskWidget(FRESHDESK_WIDGETS_ID[activeWidget]);

  return (
    <>
      <header className="sticky top-0 z-40  w-full border-b bg-white">
        <nav className=" flex items-center justify-between px-3 py-2" aria-label="Global">
          <div className="-m-1.5 flex flex-row items-center gap-3 p-1.5 ">
            <Protect roles={['SYSTEM:ADMIN', 'AGENCY:USER']}>
              <button
                type="button"
                onClick={() => setIsNavigationOpen(true)}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-label="Open navigation menu"
                aria-expanded={isNavigationOpen}
              >
                <span className="sr-only">Open navigation menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            </Protect>
            <Link href="/dashboard">
              <span className="sr-only">Fiona Posts</span>
              <Image src={HeaderLogo} alt="fiona posts" className="h-8 w-auto" />
            </Link>
            <h1 className="text-fiona text-xl font-bold leading-10">{title}</h1>
          </div>

          {!hideSearch && (
            <div className="hidden w-[400px] md:block">
              <div className="relative mt-2 rounded-md shadow-sm">
                <HeaderSearch />
              </div>
            </div>
          )}
          <div className="flex items-center gap-x-4">
            <button
              onClick={() => openSupportForm(currentUser?.email, activeWidget, freshdeskContext)}
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-lg border border-black bg-white px-3.5 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              <QuestionMarkCircleIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              Help
            </button>
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <MenuButton className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  {currentAccount?.name}
                  <UserCircleIcon aria-hidden="true" className="w5 h-5" />
                </MenuButton>
              </div>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right cursor-pointer divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <AccountSwitcher />
                <div className="py-1">
                  <Protect roles={['SYSTEM:ADMIN', 'AGENCY:USER']}>
                    <MenuItem>
                      <Link
                        href={adminHref}
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        System Admin
                      </Link>
                    </MenuItem>
                    <MenuItem>
                      <Link
                        href="/docs"
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                      >
                        API Documentation
                      </Link>
                    </MenuItem>
                  </Protect>
                  <MenuItem>
                    <p
                      key="logOut"
                      onClick={logout}
                      className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                    >
                      Sign Out
                    </p>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </nav>
      </header>
      <MainNavigation open={isNavigationOpen} onClose={setIsNavigationOpen} />
    </>
  );
}
